import React, { useContext } from 'react';
import PageWrapper from '../components/PageWrapper';
import { AuthContext } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { formatDate } from '../utils/dateUtils';

const PLAN_LABEL  = { free: 'Free', bronze: 'Bronze', silver: 'Silver', gold: 'Gold' };
const PLAN_COLOUR = { free: 'secondary', bronze: 'warning', silver: 'info', gold: 'success' };
const STATUS_COLOUR = { active: 'success', trial: 'primary', expired: 'danger', cancelled: 'secondary' };

const PLAN_FEATURES = {
  free:   ['Core property listing', 'Basic tenancy management', 'Manual payment recording'],
  bronze: ['Everything in Free', 'Bank account management', 'Charge & credit tracking', 'Occupant management'],
  silver: ['Everything in Bronze', 'Reports & analytics', 'Payment validation', 'Priority support'],
  gold:   ['Everything in Silver', 'Advanced reporting', 'API access', 'Dedicated account manager'],
};

const SubscriptionSettings = () => {
  const { user, activePersona, activeSubscription, agencyCountry } = useContext(AuthContext);
  const { plan, status, expiresAt, isActive } = useSubscription();

  const entityTypeLabel = {
    agency:      'Agency',
    owner:       'Owner',
    head_tenant: 'Head Tenant',
  }[activePersona?.type] || 'Entity';

  return (
    <PageWrapper>
      <div className="container py-4" style={{ maxWidth: 800 }}>
        <h2 className="fw-bold mb-1">
          <i className="bi bi-credit-card-2-front me-2 text-primary"></i>
          Subscription
        </h2>
        <p className="text-muted mb-4">
          Manage the subscription for <strong>{activePersona?.name || entityTypeLabel}</strong>
        </p>

        {/* Current plan card */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <div className="text-muted small mb-1">Current plan</div>
                <div className="d-flex align-items-center gap-2">
                  <span className={`badge bg-${PLAN_COLOUR[plan] || 'secondary'} fs-6 text-capitalize`}>
                    {PLAN_LABEL[plan] || plan}
                  </span>
                  {activeSubscription && (
                    <span className={`badge bg-${STATUS_COLOUR[status] || 'secondary'} text-capitalize`}>
                      {status}
                    </span>
                  )}
                </div>
              </div>

              <div className="text-end">
                {expiresAt ? (
                  <>
                    <div className="text-muted small">Expires</div>
                    <div className={`fw-medium ${isActive ? '' : 'text-danger'}`}>
                      {formatDate(expiresAt, { userPreference: user?.dateFormatPreference, agencyCountry })}
                    </div>
                  </>
                ) : (
                  <div className="text-success small fw-medium">
                    <i className="bi bi-infinity me-1"></i>No expiry
                  </div>
                )}
              </div>
            </div>

            {!isActive && (
              <div className="alert alert-warning mt-3 mb-0 py-2 small">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Your subscription is not active. Some features may be restricted.
              </div>
            )}
          </div>
        </div>

        {/* Plan comparison */}
        <h5 className="fw-semibold mb-3">Plan features</h5>
        <div className="row g-3 mb-4">
          {Object.entries(PLAN_FEATURES).map(([p, features]) => (
            <div className="col-12 col-sm-6" key={p}>
              <div className={`card h-100 border-2 ${p === plan ? 'border-primary' : 'border-light'}`}>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className={`badge bg-${PLAN_COLOUR[p]} text-capitalize`}>
                      {PLAN_LABEL[p]}
                    </span>
                    {p === plan && (
                      <span className="badge bg-primary-subtle text-primary border border-primary small">
                        Current
                      </span>
                    )}
                  </div>
                  <ul className="list-unstyled mb-0 small">
                    {features.map((f) => (
                      <li key={f} className="mb-1">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>{f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Upgrade CTA */}
        {plan !== 'gold' && (
          <div className="card border-0 bg-primary text-white shadow-sm">
            <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div>
                <h5 className="fw-bold mb-1">Ready to upgrade?</h5>
                <p className="mb-0 opacity-75 small">
                  Contact us to upgrade your plan and unlock more features.
                </p>
              </div>
              <a
                href="mailto:support@trackassets.com.au"
                className="btn btn-light fw-semibold"
              >
                <i className="bi bi-envelope me-2"></i>Contact Us
              </a>
            </div>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default SubscriptionSettings;
